/**
 * Concept Level area fields — shared by the Reference > Design > Concepts branch
 * (DesignSection.jsx LevelNode) and the CF/IDP/DAPC milestone Design nodes
 * (MilestoneSections.jsx). One canonical rendering of a level's editable area
 * categories so both contexts stay in sync.
 *
 * One export, rendered after Rooms (see ConceptLevelsSection.jsx / DesignSection.jsx's
 * levelZoom — both render `ConceptRoomsSection` then `ConceptLevelAreaMetrics`):
 *   - `ConceptLevelAreaMetrics` (named, default export dropped 2026-07) — the FAR/area breakdown
 *     group (Living Area, Garage Area, Elevated Decks, Outdoor Covered, Mechanical Storage) as
 *     expandable categories, wrapped in a single collapsible "Area Plan Metrics" node. Each area
 *     category is its own expandable node — its triangle drops down to reveal Existing/New/Remodel
 *     as independent child rows, each with its own single-field edit modal. No combined "edit all
 *     3 scopes at once" modal. Hidden entirely (via `hideAreaMetrics`) on the Conceptual
 *     Framework / Integrated Design Proposal milestones — not needed until Discretionary
 *     Approvals Plan Check Drawings.
 *
 *     A "Lot Coverage" Yes/No row (`Include in Lot Coverage` on Concept Levels) briefly lived at
 *     the bottom of this node (2026-07) but was removed the same round: Development Metrics
 *     (ConceptDevMetricsSection.jsx) always uses the manually-entered `Proposed Lot Coverage Area
 *     Override` now, never the Airtable-computed rollup this checkbox fed — showing the flag here
 *     would imply it drives a number the app no longer reads. The Airtable field itself and its
 *     rollup chain are untouched, just unused by the app.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { wf, saveViewState } from '../outline/state.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'

export const LEVEL_FIELD_ROWS = [
  {
    rowLabel: 'Living Area',
    totalField: 'Total Living Area',
    editFields: [
      { field: 'Existing Living Area', label: 'Existing', type: 'number' },
      { field: 'New Living Area',      label: 'New',      type: 'number' },
      { field: 'Remodel Living Area',  label: 'Remodel',  type: 'number' },
    ],
  },
  {
    rowLabel: 'Garage Area',
    totalField: 'Total Garage Area',
    editFields: [
      { field: 'Existing Garage Area', label: 'Existing', type: 'number' },
      { field: 'New Garage Area',      label: 'New',      type: 'number' },
      { field: 'Remodel Garage Area',  label: 'Remodel',  type: 'number' },
    ],
  },
  {
    rowLabel: 'Elevated Decks',
    totalField: 'Total Elevated Decks Area',
    editFields: [
      { field: 'Existing Elevated Decks Area', label: 'Existing', type: 'number' },
      { field: 'New Elevated Decks Area',      label: 'New',      type: 'number' },
      { field: 'Remodel Elevated Decks Area',  label: 'Remodel',  type: 'number' },
    ],
  },
  {
    rowLabel: 'Outdoor Covered',
    totalField: null,
    editFields: [
      { field: 'Existing Outdoor Covered Area', label: 'Existing', type: 'number' },
      { field: 'New Outdoor Covered Area',      label: 'New',      type: 'number' },
      { field: 'Remodel Outdoor Covered Area',  label: 'Remodel',  type: 'number' },
    ],
  },
  {
    rowLabel: 'Mechanical Storage',
    totalField: 'Total Mechanical Storage Area',
    editFields: [
      { field: 'Existing Mechanical Storage Area', label: 'Existing', type: 'number' },
      { field: 'New Mechanical Storage Area',      label: 'New',      type: 'number' },
      { field: 'Remodel Mechanical Storage Area',  label: 'Remodel',  type: 'number' },
    ],
  },
]

export function levelFieldDisplayValue(row, levelFields) {
  if (row.totalField) {
    const v = levelFields[row.totalField] || 0
    return v > 0 ? v.toLocaleString() + ' sf' : '—'
  }
  // Outdoor Covered: manual sum (no formula field)
  const total = row.editFields.reduce((sum, ef) => sum + (levelFields[ef.field] || 0), 0)
  return total > 0 ? total.toLocaleString() + ' sf' : '—'
}

function fmtScopeValue(ef, levelFields) {
  const v = levelFields[ef.field]
  if (v == null || v === 0) return '—'
  const num = ef.precision ? Number(v).toFixed(ef.precision) : String(v)
  return ef.type === 'number' && !ef.precision ? num + ' sf' : num
}

/* ── ScopeFieldEditModal — single-field edit form ────────────────────────────────────────────── */
function ScopeFieldEditModal({ label, ef, levelFields, onSave, onClose }) {
  const [draft, setDraft] = useState(() => levelFields[ef.field] ?? null)
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      await onSave({ [ef.field]: draft })
      onClose()
    } catch {
      setSaving(false)
    }
  }

  const numInp = {
    fontFamily: '"DM Mono",monospace', fontSize: 12,
    padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)',
    borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)',
    outline: 'none', textAlign: 'right', width: 120, boxSizing: 'border-box',
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 360, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 18 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit {label}
        </div>
        {ef.type === 'number' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              autoFocus type="number" min={0}
              step={ef.precision ? Math.pow(10, -ef.precision) : 1}
              value={draft ?? ''}
              onChange={e => setDraft(e.target.value === '' ? null : Number(e.target.value))}
              style={numInp}
            />
            {!ef.precision && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' }}>sf</span>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── ScopeFieldRow — leaf row for one scope field (Existing/New/Remodel) ─────────────────────── */
function ScopeFieldRow({ label, ef, levelFields, onEdit, indented, dataKey }) {
  const menu = useActionMenu()
  const display = fmtScopeValue(ef, levelFields)
  const isEmpty = display === '—'
  return (
    <div className="wf-node wf-node--item" data-key={dataKey}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        {indented && <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />}
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{label}</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: isEmpty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
          }}>{display}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onEdit(label, ef) }}>Edit</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/* ── AreaCategoryRow — expandable category (Living Area, Garage Area, …) ────────────────────────
   No edit action of its own — each scope child is edited independently. */
function AreaCategoryRow({ row, levelId, levelFields, onEdit }) {
  const key = 'level-area:' + levelId + ':' + row.rowLabel
  const [collapsed, setCollapsed] = useState(() => wf.collapsed[key] !== false)
  function toggle() { setCollapsed(c => { const n = !c; wf.collapsed[key] = n; saveViewState(); return n }) }
  const displayValue = levelFieldDisplayValue(row, levelFields)
  const isEmpty = displayValue === '—'
  return (
    <div className={`wf-node wf-node--cat${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <span className="wf-bullet wf-bullet--cat" style={{ cursor: 'default' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">{row.rowLabel}</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: isEmpty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
          }}>{displayValue}</span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {row.editFields.map(ef => (
            <ScopeFieldRow key={ef.field} dataKey={key + ':' + ef.field} label={ef.label} ef={ef} levelFields={levelFields} onEdit={onEdit} indented />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Renders the FAR/area breakdown group (Living Area, Garage Area, Elevated Decks, Outdoor
 * Covered, Mechanical Storage) under a single collapsible "Area Plan Metrics" node. Not needed
 * until Discretionary Approvals Plan Check Drawings — pass `hideAreaMetrics` to omit it entirely
 * on the Conceptual Framework / Integrated Design Proposal milestones.
 * onSave(levelId, fieldsPatch) — caller owns persistence + cache refresh.
 */
export function ConceptLevelAreaMetrics({ level, onSave, hideAreaMetrics }) {
  const [editing, setEditing] = useState(null) // { label, ef } | null
  const key = 'level-area-metrics:' + level.id
  const [collapsed, setCollapsed] = useState(() => wf.collapsed[key] !== false)
  function toggle() { setCollapsed(c => { const n = !c; wf.collapsed[key] = n; saveViewState(); return n }) }

  async function handleSave(updates) {
    await onSave(level.id, updates)
  }

  if (hideAreaMetrics) return null

  return (
    <div className={`wf-node wf-node--cat${collapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${collapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <span className="wf-bullet wf-bullet--cat" style={{ cursor: 'default' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">Area Plan Metrics</span>
        </div>
      </div>
      {!collapsed && (
        <div className="wf-children">
          {LEVEL_FIELD_ROWS.map(row => (
            <AreaCategoryRow
              key={row.rowLabel} row={row} levelId={level.id} levelFields={level.fields}
              onEdit={(label, ef) => setEditing({ label, ef })}
            />
          ))}
        </div>
      )}
      {editing && (
        <ScopeFieldEditModal
          label={editing.label} ef={editing.ef} levelFields={level.fields}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
